import api from './client';
import type { AvailabilityBlock, AvailabilityBlockType } from "@store/disponibiliteStore";

/* ── API shape (snake_case) ── */

interface ApiDisponibiliteBlock {
  id: string;
  org_id: string;
  worker_id: string;
  worker_name: string;
  type: "recurring" | "ponctuel";
  block_type: string;
  day_of_week?: number;
  date?: string;
  start_hour: number;
  end_hour: number;
  label: string;
  color: string;
  category: "disponibilite" | "service_rapide";
}

/* ── Converters ── */

function toFrontend(b: ApiDisponibiliteBlock): AvailabilityBlock {
  return {
    id: b.id,
    workerId: b.worker_id,
    label: b.label,
    type: (b.block_type || "off") as AvailabilityBlockType,
    date: b.date || "",
    startHour: b.start_hour,
    durationHours: b.end_hour - b.start_hour,
    recurring: b.type === "recurring",
    dayOfWeek: b.day_of_week,
  };
}

function toApi(b: Omit<AvailabilityBlock, "id">, orgId: string, category: string, workerName: string = ""): Omit<ApiDisponibiliteBlock, "id"> {
  return {
    org_id: orgId,
    worker_id: b.workerId,
    worker_name: workerName,
    type: b.recurring ? "recurring" : "ponctuel",
    block_type: b.type,
    day_of_week: b.dayOfWeek,
    date: b.date || undefined,
    start_hour: b.startHour,
    end_hour: b.startHour + b.durationHours,
    label: b.label,
    color: "#3b82f6",
    category: category as "disponibilite" | "service_rapide",
  };
}

function toApiUpdate(updates: Partial<Omit<AvailabilityBlock, "id">>): Record<string, any> {
  const out: Record<string, any> = {};
  if (updates.workerId !== undefined) out.worker_id = updates.workerId;
  if (updates.label !== undefined) out.label = updates.label;
  if (updates.type !== undefined) out.block_type = updates.type;
  if (updates.recurring !== undefined) out.type = updates.recurring ? "recurring" : "ponctuel";
  if (updates.dayOfWeek !== undefined) out.day_of_week = updates.dayOfWeek;
  if (updates.date !== undefined) out.date = updates.date;
  if (updates.startHour !== undefined) out.start_hour = updates.startHour;
  if (updates.durationHours !== undefined && updates.startHour !== undefined) {
    out.end_hour = updates.startHour + updates.durationHours;
  } else if (updates.durationHours !== undefined) {
    // Can't compute end_hour without start_hour, caller should provide both
    out.end_hour = updates.durationHours; // fallback
  }
  return out;
}

/* ── API calls ── */

export const disponibilitesApi = {
  list: async (params?: { org_id?: string; worker_id?: string; category?: string; limit?: number }): Promise<AvailabilityBlock[]> => {
    const { data } = await api.get('/disponibilites', { params });
    return (data as ApiDisponibiliteBlock[]).map(toFrontend);
  },

  create: async (block: Omit<AvailabilityBlock, "id">, orgId: string, category: string, workerName?: string): Promise<AvailabilityBlock> => {
    const payload = toApi(block, orgId, category, workerName);
    const { data } = await api.post('/disponibilites', payload);
    return toFrontend(data);
  },

  update: async (id: string, updates: Partial<Omit<AvailabilityBlock, "id">>): Promise<AvailabilityBlock> => {
    const payload = toApiUpdate(updates);
    const { data } = await api.patch(`/disponibilites/${id}`, payload);
    return toFrontend(data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/disponibilites/${id}`);
  },
};
